import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SaveStatus, LoadingButton } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Pencil, History, X, Power, Trash2, Merge, Crosshair } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]; // 0..6, week starts Monday
const DAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const weeklyHours = (opening = "10:00", closing = "22:00", closedDays = []) =>
  DAYS.map((_, day) => ({ day, closed: closedDays.includes(day), opening_time: opening, closing_time: closing }));

const normalizedWeeklyHours = (branch) => {
  if (branch?.weekly_hours?.length === 7) {
    return [...branch.weekly_hours].sort((a, b) => a.day - b.day);
  }
  return weeklyHours(branch?.opening_time, branch?.closing_time, branch?.closed_days || []);
};

const blank = () => ({
  code: "", name: "", location: "", aliases: [], active: true,
  opening_time: "10:00", closing_time: "22:00", closed_days: [],
  weekly_hours: weeklyHours(),
  special_closures: [], temp_hours: [], daily_target: 0,
  low_balance_threshold: 100, responsible_people: [],
  latitude: "", longitude: "", attendance_radius_m: 200,
  gps_accuracy_limit_m: 100, geofence_enabled: false,
});

function BranchDialog({ branch, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(blank());
  const [aliasText, setAliasText] = useState("");
  const [respText, setRespText] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [gpsBusy, setGpsBusy] = useState(false);

  useEffect(() => {
    if (open) {
      const b = branch || blank();
      setForm({ ...blank(), ...b, weekly_hours: normalizedWeeklyHours(b) });
      setAliasText((b.aliases || []).join(", "));
      setRespText((b.responsible_people || []).join(", "));
      setStatus("idle"); setError("");
    }
  }, [open, branch]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateWeeklyDay = (day, key, value) => set("weekly_hours", form.weekly_hours.map((item) => item.day === day ? { ...item, [key]: value } : item));

  const addClosure = () => set("special_closures", [...form.special_closures, { date: "", reason: "" }]);
  const addTemp = () => set("temp_hours", [...form.temp_hours, { date: "", opening_time: "10:00", closing_time: "22:00", reason: "" }]);

  const useCurrentGps = () => {
    if (!navigator.geolocation) return toast.error("GPS is not supported on this device");
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setForm((current) => ({ ...current, latitude: Number(coords.latitude.toFixed(7)), longitude: Number(coords.longitude.toFixed(7)) }));
        toast.success(`Outlet GPS captured (accuracy ±${Math.round(coords.accuracy)}m)`);
        setGpsBusy(false);
      },
      () => { toast.error("Allow location access and try again at the outlet"); setGpsBusy(false); },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  };

  const save = async () => {
    if (!form.code || !form.name) { setStatus("failed"); setError("Code and name required"); return; }
    if (form.geofence_enabled && (form.latitude === "" || form.longitude === "")) { setStatus("failed"); setError("Capture or enter outlet GPS before enabling geofence"); return; }
    setStatus("saving"); setError("");
    const payload = {
      ...form,
      aliases: aliasText.split(",").map((a) => a.trim()).filter(Boolean),
      responsible_people: respText.split(",").map((a) => a.trim()).filter(Boolean),
      daily_target: Number(form.daily_target || 0),
      low_balance_threshold: Number(form.low_balance_threshold || 0),
      latitude: form.latitude === "" || form.latitude == null ? null : Number(form.latitude),
      longitude: form.longitude === "" || form.longitude == null ? null : Number(form.longitude),
      attendance_radius_m: Number(form.attendance_radius_m || 200),
      gps_accuracy_limit_m: Number(form.gps_accuracy_limit_m || 100),
      weekly_hours: form.weekly_hours,
      special_closures: form.special_closures.filter((c) => c.date),
      temp_hours: form.temp_hours.filter((t) => t.date),
    };
    try {
      if (branch) await api.put(`/branches/${branch.id}`, payload);
      else await api.post("/branches", payload);
      setStatus("saved"); toast.success("Branch saved"); onSaved();
      setTimeout(() => onOpenChange(false), 400);
    } catch (e) { setStatus("failed"); setError(apiError(e)); toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{branch ? "Edit Branch" : "Add Branch"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Code *</Label><Input value={form.code} onChange={(e) => set("code", e.target.value)} data-testid="branch-code" /></div>
            <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="branch-name" /></div>
            <div className="col-span-2"><Label className="text-xs">Location</Label><Input value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="branch-location" /></div>
          </div>
          <div><Label className="text-xs">Aliases (comma separated — source systems map to this branch)</Label>
            <Input value={aliasText} onChange={(e) => setAliasText(e.target.value)} placeholder="Shamelin Drift, 1 Shamelin" data-testid="branch-aliases" /></div>
          <div className="rounded-sm border border-blue-200 bg-blue-50/40 p-3 space-y-3">
            <div className="flex items-center justify-between gap-3"><div><Label className="text-sm font-semibold">Attendance GPS Geofence</Label><p className="text-[11px] text-slate-500 mt-0.5">Clock in and out must be within this outlet radius when enabled.</p></div><Switch checked={form.geofence_enabled} onCheckedChange={(value) => set("geofence_enabled", value)} data-testid="branch-geofence-enabled" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Outlet Latitude</Label><Input type="number" step="any" value={form.latitude ?? ""} onChange={(e) => set("latitude", e.target.value)} placeholder="3.1234567" data-testid="branch-latitude" /></div>
              <div><Label className="text-xs">Outlet Longitude</Label><Input type="number" step="any" value={form.longitude ?? ""} onChange={(e) => set("longitude", e.target.value)} placeholder="101.1234567" data-testid="branch-longitude" /></div>
              <div><Label className="text-xs">Allowed Radius (metres)</Label><Input type="number" min="50" max="2000" value={form.attendance_radius_m} onChange={(e) => set("attendance_radius_m", e.target.value)} data-testid="branch-geofence-radius" /></div>
              <div><Label className="text-xs">Maximum GPS Error (metres)</Label><Input type="number" min="10" max="1000" value={form.gps_accuracy_limit_m} onChange={(e) => set("gps_accuracy_limit_m", e.target.value)} data-testid="branch-gps-accuracy" /></div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={useCurrentGps} disabled={gpsBusy}><Crosshair className="h-4 w-4 mr-1" />{gpsBusy ? "Detecting GPS…" : "Use current GPS at outlet"}</Button>
          </div>
          <div><Label className="text-xs">Daily Target (RM)</Label><Input type="number" value={form.daily_target} onChange={(e) => set("daily_target", e.target.value)} data-testid="branch-daily-target" /></div>
          <div className="rounded-sm border border-slate-200 p-3 space-y-2">
            <div><Label className="text-sm font-semibold">Weekly Operating Hours</Label><p className="text-[11px] text-slate-500 mt-0.5">Set different hours for each day. Changes apply only to this branch.</p></div>
            <div className="grid grid-cols-[110px_1fr_1fr_90px] gap-2 px-1 text-[10px] uppercase tracking-wider text-slate-400">
              <span>Day</span><span>Opening</span><span>Closing</span><span>Closed</span>
            </div>
            {form.weekly_hours.map((item) => <div key={item.day} className="grid grid-cols-[110px_1fr_1fr_90px] gap-2 items-center">
              <span className="text-sm font-medium">{DAY_LABELS[item.day]}</span>
              <Input type="time" value={item.opening_time} disabled={item.closed} onChange={(e) => updateWeeklyDay(item.day, "opening_time", e.target.value)} data-testid={`branch-open-${item.day}`} />
              <Input type="time" value={item.closing_time} disabled={item.closed} onChange={(e) => updateWeeklyDay(item.day, "closing_time", e.target.value)} data-testid={`branch-close-${item.day}`} />
              <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={item.closed} onCheckedChange={(value) => updateWeeklyDay(item.day, "closed", Boolean(value))} data-testid={`closed-day-${DAYS[item.day]}`} /> Closed</label>
            </div>)}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Petty Cash Low-Balance Threshold (RM)</Label><Input type="number" value={form.low_balance_threshold} onChange={(e) => set("low_balance_threshold", e.target.value)} data-testid="branch-threshold" /></div>
            <div><Label className="text-xs">Responsible People (comma separated)</Label><Input value={respText} onChange={(e) => setRespText(e.target.value)} placeholder="Ahmad, Siti" data-testid="branch-responsible" /></div>
          </div>
          <div>
            <div className="flex items-center justify-between"><Label className="text-xs">Special Closure Dates</Label>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addClosure} data-testid="add-closure"><Plus className="h-3 w-3 mr-1" />Add</Button></div>
            {form.special_closures.map((c, i) => (
              <div key={i} className="flex gap-2 mt-2 items-center">
                <Input type="date" value={c.date} onChange={(e) => set("special_closures", form.special_closures.map((x, j) => j === i ? { ...x, date: e.target.value } : x))} className="w-40" data-testid={`closure-date-${i}`} />
                <Input placeholder="Reason" value={c.reason} onChange={(e) => set("special_closures", form.special_closures.map((x, j) => j === i ? { ...x, reason: e.target.value } : x))} data-testid={`closure-reason-${i}`} />
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => set("special_closures", form.special_closures.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
          <div>
            <div className="flex items-center justify-between"><Label className="text-xs">Temporary Operating Hours</Label>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addTemp} data-testid="add-temp"><Plus className="h-3 w-3 mr-1" />Add</Button></div>
            {form.temp_hours.map((t, i) => (
              <div key={i} className="flex gap-2 mt-2 items-center">
                <Input type="date" value={t.date} onChange={(e) => set("temp_hours", form.temp_hours.map((x, j) => j === i ? { ...x, date: e.target.value } : x))} className="w-36" data-testid={`temp-date-${i}`} />
                <Input type="time" value={t.opening_time} onChange={(e) => set("temp_hours", form.temp_hours.map((x, j) => j === i ? { ...x, opening_time: e.target.value } : x))} className="w-28" />
                <Input type="time" value={t.closing_time} onChange={(e) => set("temp_hours", form.temp_hours.map((x, j) => j === i ? { ...x, closing_time: e.target.value } : x))} className="w-28" />
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => set("temp_hours", form.temp_hours.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter className="items-center">
          <SaveStatus status={status} error={error} />
          <LoadingButton loading={status === "saving"} onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="branch-save-btn">Save Branch</LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AuditDialog({ branchId, open, onOpenChange }) {
  const [logs, setLogs] = useState([]);
  useEffect(() => { if (open && branchId) api.get(`/branches/${branchId}/audit`).then(({ data }) => setLogs(data)).catch(() => {}); }, [open, branchId]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Audit History</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {logs.length === 0 ? <div className="text-sm text-slate-400 py-6 text-center">No history.</div> :
            logs.map((l) => (
              <div key={l.id} className="text-sm border-b border-slate-100 pb-2" data-testid={`audit-${l.id}`}>
                <div className="flex justify-between"><span className="font-medium capitalize">{l.action}</span><span className="text-xs text-slate-400">{fmtDateTime(l.timestamp)}</span></div>
                <div className="text-xs text-slate-500">{l.user_name}{l.note ? ` · ${l.note}` : ""}</div>
              </div>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeleteBranchDialog({ branch, branches, open, onOpenChange, onCompleted }) {
  const [impact, setImpact] = useState(null);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !branch) return;
    setImpact(null); setTarget("");
    api.get(`/branches/${branch.id}/delete-impact`).then(({ data }) => setImpact(data))
      .catch((error) => { toast.error(apiError(error)); onOpenChange(false); });
  }, [open, branch, onOpenChange]);

  const hardDelete = async () => {
    if (!window.confirm(`Permanently delete ${branch.name} (${branch.code})? This cannot be undone.`)) return;
    setBusy(true);
    try { await api.delete(`/branches/${branch.id}`); toast.success("Branch deleted"); onOpenChange(false); await onCompleted(); }
    catch (error) { toast.error(apiError(error)); }
    finally { setBusy(false); }
  };

  const merge = async () => {
    const destination = branches.find((item) => item.id === target);
    if (!destination) return toast.error("Choose the destination branch");
    if (!window.confirm(`Merge every linked record from ${branch.name} into ${destination.name}, then permanently delete ${branch.name}?`)) return;
    setBusy(true);
    try { await api.post(`/branches/${branch.id}/merge`, { target_branch_id: target }); toast.success(`Merged into ${destination.name}`); onOpenChange(false); await onCompleted(); }
    catch (error) { toast.error(apiError(error)); }
    finally { setBusy(false); }
  };

  const linked = impact?.total > 0;
  return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Delete Branch</DialogTitle></DialogHeader>
    <div className="space-y-4">
      <div className="rounded border bg-slate-50 p-3"><div className="font-semibold">{branch?.name} <span className="font-mono text-sm text-slate-500">({branch?.code})</span></div><p className="mt-1 text-xs text-slate-500">The branch is identified by stable ID. Editing it updates this same record; deletion removes the ID after all references are checked.</p></div>
      {!impact && <div className="py-8 text-center text-sm text-slate-400">Checking linked records…</div>}
      {impact && <>
        <div><div className="text-sm font-semibold mb-2">Linked records: {impact.total}</div><div className="grid grid-cols-2 gap-2">{Object.entries(impact.counts).filter(([, count]) => count > 0).map(([name, count]) => <div key={name} className="flex justify-between rounded border px-3 py-2 text-xs"><span>{name.replaceAll("_", " ")}</span><strong>{count}</strong></div>)}{impact.total === 0 && <div className="col-span-2 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">No linked records. Hard delete is available.</div>}</div></div>
        {linked && <div className="rounded border border-amber-200 bg-amber-50 p-3 space-y-2"><p className="text-sm text-amber-900">Direct deletion is blocked. Select the correct surviving branch; all references will be moved before this duplicate is removed.</p><Select value={target || undefined} onValueChange={setTarget}><SelectTrigger><SelectValue placeholder="Merge into another branch" /></SelectTrigger><SelectContent>{branches.filter((item) => item.id !== branch.id && item.active !== false).map((item) => <SelectItem key={item.id} value={item.id}>{item.name} ({item.code})</SelectItem>)}</SelectContent></Select></div>}
      </>}
    </div>
    <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>{impact && (linked ? <Button disabled={!target || busy} onClick={merge}><Merge className="h-4 w-4 mr-1" />{busy ? "Merging…" : "Merge & Delete"}</Button> : <Button variant="destructive" disabled={busy} onClick={hardDelete}><Trash2 className="h-4 w-4 mr-1" />{busy ? "Deleting…" : "Delete permanently"}</Button>)}</DialogFooter>
  </DialogContent></Dialog>;
}

export default function BranchMaster() {
  const { can, loadBranches } = useAuth();
  const canEditBranch = can("branch.edit") || can("branch.manage");
  const canManageBranch = can("branch.manage");
  const [branches, setBranches] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [auditFor, setAuditFor] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/branches"); setBranches(data); loadBranches(); } catch (e) { toast.error(apiError(e)); }
  }, [loadBranches]);
  useEffect(() => { load(); }, []);

  const toggleActive = async (b) => {
    try { await api.post(`/branches/${b.id}/deactivate`); toast.success(b.active ? "Deactivated (records preserved)" : "Reactivated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Branch Master" subtitle="Central source of truth. All modules reference branch IDs. Aliases map source-system names to the correct branch."
        testid="branch-header"
        actions={canManageBranch && <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-branch-btn"><Plus className="h-4 w-4 mr-1" /> Add Branch</Button>} />

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="py-3 px-4">Code</th><th className="py-3 px-4">Name</th><th className="py-3 px-4">Location</th>
            <th className="py-3 px-4">Hours</th><th className="py-3 px-4">Closed Days</th><th className="py-3 px-4">Aliases</th>
            <th className="py-3 px-4">Status</th><th className="py-3 px-4"></th>
          </tr></thead>
          <tbody>
            {branches.map((b) => (
              <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`branch-row-${b.code}`}>
                <td className="py-3 px-4 font-mono text-xs font-semibold">{b.code}</td>
                <td className="py-3 px-4 font-medium text-slate-800">{b.name}</td>
                <td className="py-3 px-4 text-slate-500">{b.location || "—"}</td>
                <td className="py-3 px-4 text-slate-500">{b.weekly_hours?.length === 7 ? "Set by weekday" : `${b.opening_time}–${b.closing_time}`}</td>
                <td className="py-3 px-4">{(b.weekly_hours?.length === 7 ? b.weekly_hours.filter((item) => item.closed).map((item) => item.day) : b.closed_days || []).length ? (b.weekly_hours?.length === 7 ? b.weekly_hours.filter((item) => item.closed).map((item) => DAYS[item.day]) : b.closed_days.map((d) => DAYS[d])).join(", ") : <span className="text-slate-300">none</span>}</td>
                <td className="py-3 px-4 max-w-[220px]"><div className="flex flex-wrap gap-1">{(b.aliases || []).slice(0, 4).map((a) => <Badge key={a} variant="secondary" className="text-[10px]">{a}</Badge>)}{b.aliases?.length > 4 && <span className="text-[10px] text-slate-400">+{b.aliases.length - 4}</span>}</div></td>
                <td className="py-3 px-4">{b.active ? <Badge className="bg-emerald-100 text-emerald-800">Active</Badge> : <Badge className="bg-slate-200 text-slate-600">Inactive</Badge>}</td>
                <td className="py-3 px-4 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAuditFor(b.id)} data-testid={`audit-btn-${b.code}`}><History className="h-3.5 w-3.5" /></Button>
                  {canEditBranch && <Button size="sm" variant="ghost" className="h-7 text-xs text-blue-600" onClick={() => { setEditing(b); setDialogOpen(true); }} data-testid={`edit-branch-${b.code}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                  {canManageBranch && <>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => toggleActive(b)} data-testid={`toggle-branch-${b.code}`}><Power className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={() => setDeleting(b)} data-testid={`delete-branch-${b.code}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <BranchDialog branch={editing} open={dialogOpen} onOpenChange={setDialogOpen} onSaved={load} />
      <AuditDialog branchId={auditFor} open={!!auditFor} onOpenChange={(o) => !o && setAuditFor(null)} />
      <DeleteBranchDialog branch={deleting} branches={branches} open={!!deleting} onOpenChange={(value) => !value && setDeleting(null)} onCompleted={load} />
    </div>
  );
}
